import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { DataService, AuthService, ToasterService } from 'src/app/shared/services';
import { LifecycleModelModel } from 'src/app/shared/models';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { validateNumberInput } from 'src/utils';
import { CoreImageUploadComponent } from 'src/app/shared/core-image-upload/core-image-upload.component';
import { CoreImageSliderComponent } from 'src/app/shared/core-image-slider/core-image-slider.component';
import { GenericModalComponent } from 'src/app/shared/modals/generic-modal.component';

@Component({
  selector: 'app-lifecycle-model-edit',
  templateUrl: './edit.component.html'
})
export class LifecycleModelEditComponent implements OnInit {
  @ViewChild(CoreImageUploadComponent)
  coreImageUploadComp!: CoreImageUploadComponent;

  // Modal parameters
  items: any; // array of items to edit
  // form instance
  form!: UntypedFormGroup;

  // Usage mode
  mode: ('mass' | 'single' | 'new') = 'single';

  // Target Id's
  targetIds: number[] = [];

  // UI Flag
  isLoading = false;
  data = null;
  isSubmitted = false;
  showValidationError = false;

  // Form reference Data
  parentReference: any;

  // Image upload modal
  hasExistingImages = false;
  imagesLoaded = false;
  imageUploadModalRef?: BsModalRef;
  @ViewChild('imageUploadBody') imageUploadBody!: TemplateRef<any>;
  @ViewChild('modalUploadComp') modalUploadComp!: CoreImageUploadComponent;
  @ViewChild('imageSlider') imageSliderComp!: CoreImageSliderComponent;

  /**
   * @param formBuilder
   * @param service
   * @param auth
   * @param bsModalRef
   * @param translate
   */
  constructor(
    private formBuilder: UntypedFormBuilder,
    protected service: DataService,
    public auth: AuthService,
    public bsModalRef: BsModalRef,
    public translate: TranslateService,
    private toastr: ToasterService,
    private bsModalService: BsModalService
  ) {
    this.isLoading = true;
  }

  /**
   *
   */
  async ngOnInit(): Promise<void> {

    // Sniff mode
    this.mode = this.sniffEditMode(this.items);

    // get target Ids
    this.targetIds = _.map(this.items, el => el.id);

    // Get default empty object
    if (['mass', 'new'].indexOf(this.mode) > -1) {
      this.items = [new LifecycleModelModel({
        site: this.auth.getSiteId(),
      })];
    }

    // Build for
    this.form = this.getForm(this.items[0]);

    if (this.mode !== 'single' || !this.targetIds[0]) {
      this.imagesLoaded = true;
    }

    // Loading done !
    this.isLoading = false;
  }

  /**
   * Sniff usage mode from items configuration
   */
  sniffEditMode(items: [LifecycleModelModel?]): ('mass' | 'single' | 'new') {
    // Empty array => New Item
    if (items === undefined || items.length === 0) {
      return 'new';

      // Else => Single Edit
    } else {
      return 'single';
    }
  }

  /**
   *
   */
  getForm(item: LifecycleModelModel): UntypedFormGroup {
    return this.formBuilder.group({
      id: new UntypedFormControl(item.id),
      site: new UntypedFormControl(this.auth.getSiteId()),
      description: new UntypedFormControl(item.description, [Validators.required, Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)]),
      businessRules: new UntypedFormControl(item.businessRules),
      comment: new UntypedFormControl(item.comment),
      expectedLife: new UntypedFormControl(item.expectedLife, [Validators.min(0)]),
      typeEolDesc: new UntypedFormControl(item.typeEolDesc),
    });
  }

  validateNumberInput(event: KeyboardEvent): void {
    validateNumberInput(event);
  }

  /**
   * Confirm callback with updated res
   */
  confirmFn = (res: any) => { };

  /**
   *
   */
  save(): boolean | void {
    this.showValidationError = true;
    if (!this.form || (this.form && !this.form.valid)) {
      return false;

    } else {

      // Query pointer
      let query;

      if (this.mode === 'new') {
        const tmpData = _.pickBy(this.form.value, el => el !== null);
        query = this.service.post('api/fastlc/lifecycle-models', tmpData);

      } else if (this.mode === 'single') {
        query = this.service.update('api/fastlc/lifecycle-models', this.targetIds[0], this.form.value);

      }

      this.isSubmitted = true;
      query?.subscribe(res => {
        const modelId = this.mode === 'new' ? res?.id : this.targetIds[0];
        if (this.mode === 'new' && modelId) {
          this.targetIds[0] = modelId;
        }
        this.confirmFn(res);
        const uploadComp = this.coreImageUploadComp;
        if (
          (this.mode === 'new' || this.mode === 'single') &&
          modelId &&
          uploadComp
        ) {
          uploadComp.uploadImages('fastlc_model', modelId);
        } else {
          this.isSubmitted = false;
          this.bsModalRef.hide();
        }
      }, err => {
        console.log('err: ', err);
        this.isSubmitted = false;
      });
    }
  }

  onSliderImagesChange(images: any[]): void {
    this.hasExistingImages = images.length > 0;
    this.imagesLoaded = true;
  }

  onFormImagesUploaded(event?: any): void {
    this.isSubmitted = false;
    if (event?.status === 'success' && event?.data?.length > 0) {
      (this as any).onImagesUploadFn?.(this.targetIds[0]);
    }
    this.bsModalRef.hide();
  }

  /**
   * Open the image upload modal when there are existing images.
   * The "Upload" button directly uploads images to the existing model.
   */
  openImageUploadModal(): void {
    const modalRef = this.bsModalService.show(GenericModalComponent, {
      class: 'modal-lg',
      initialState: {
        title: 'headers.fastlc.lifecycle_model.images',
        bodyTemplate: this.imageUploadBody,
        cancelText: 'navigations.cancel',
        confirmVisible: true,
        confirmText: 'core_image_upload.upload',
      },
    });
    this.imageUploadModalRef = modalRef;
    if (modalRef.content) {
      modalRef.content.confirmFn = () => {
        if (this.modalUploadComp) {
          this.modalUploadComp.uploadImages('fastlc_model', this.targetIds[0]);
        }
      };
    }
  }

  /**
   * Handles upload completion from the modal — closes only the image upload
   * modal without touching the edit modal.
   */
  onModalImagesUploaded(event: any): void {
    if (event.status !== 'success') {
      this.toastr.showError('messages.images_upload_error');
      return;
    }
    this.toastr.showSuccess('messages.images_upload_success');
    if (event?.data?.length > 0) {
      (this as any).onImagesUploadFn?.(this.targetIds[0]);
    }
    this.imageUploadModalRef?.hide();
    if (this.imageSliderComp) {
      this.imageSliderComp.reload();
    }
  }
}
